import { useState } from 'react';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { confirmAction } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { doctorDisplayName } from '../../lib/doctor-name';
import type { BillableAdmission } from './billable-admission';
import { useAddIpdBillRefund, useIpdBills } from './ipd-daycare.queries';
import { currency, formatIpdDateTime } from './ipd-daycare.utils';
import type { IpdBillStatus } from './types';

const statusLabel = (status: IpdBillStatus) => status === 'due' ? 'Due' : status === 'cancelled' ? 'Cancelled' : 'Paid';

const RefundPanel = ({ billId, availableCredit }: { billId: string; availableCredit: number }) => {
  const [amount, setAmount] = useState(0);
  const mutation = useAddIpdBillRefund();
  const { showToast } = useToast();

  const submit = async () => {
    if (amount <= 0 || amount > availableCredit) return;
    const confirmed = await confirmAction({
      title: 'Confirm Refund',
      description: `Are you sure you want to record a refund of ${currency(amount)} for this bill?`,
      confirmLabel: 'Yes, Record Refund',
      tone: 'danger',
    });
    if (!confirmed) return;
    mutation.mutate({ billId, amount }, {
      onSuccess: () => {
        setAmount(0);
        showToast('Refund saved successfully.', 'success');
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The refund could not be recorded.', 'error'),
    });
  };

  return (
    <section className="opd-bill-subsection">
      <h3>Refund</h3>
      <div className="opd-receipt-row">
        <label>Amount (max {currency(availableCredit)})<input type="number" min={0} max={availableCredit} step="0.01" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label>
        <button className="button button-secondary" type="button" disabled={amount <= 0 || amount > availableCredit || mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? 'Saving…' : 'Refund'}</button>
      </div>
    </section>
  );
};

export const IpdBillDetailsView = ({ admission, selectedBillId, onCreateBill, showRefundPanel = true }: {
  admission: BillableAdmission;
  selectedBillId: string | null;
  onCreateBill: () => void;
  showRefundPanel?: boolean;
}) => {
  const query = useIpdBills(admission.id);
  const canCreateBill = admission.status !== 'cancelled';
  const noBillState = (
    <EmptyState
      title="No bills yet"
      message="No bill has been created for this admission."
      {...(canCreateBill ? { action: { label: 'Create New Bill', onClick: onCreateBill } } : {})}
    />
  );

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState title="Unable to load bills" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} />;
  const bills = query.data ?? [];
  if (bills.length === 0) {
    return noBillState;
  }
  const bill = bills.find((item) => item.id === selectedBillId) ?? bills[0];
  if (!bill) return noBillState;

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row">
          <div className="opd-bill-banner-fields">
            <span>Bill No : {bill.billNumber}</span>
            <span>Bill Date : {formatIpdDateTime(bill.billDate)}</span>
            <span>Bill Section : {bill.section}</span>
          </div>
          <span className={`opd-badge ${bill.status}`}>{statusLabel(bill.status)}</span>
        </div>
        <p>Bill Created By - {bill.createdBy.name}{bill.editedBy ? ` || Last Edit By - ${bill.editedBy.name}` : ''}</p>
      </header>

      <div className="table-scroll">
        <table className="opd-bill-charge-table">
          <thead><tr><th>#</th><th>Date</th><th>Charge Name</th><th>Charge Amount</th><th>Qty</th><th>Dis Amt</th><th>Dis %</th><th>Status</th><th>Total</th></tr></thead>
          <tbody>
            {bill.charges.length === 0
              ? <tr><td className="opd-bill-empty" colSpan={9}>No charges recorded on this bill.</td></tr>
              : bill.charges.map((line, index) => (
                <tr key={line.id} className={line.cancelled ? undefined : 'paid'}>
                  <td>{index + 1}</td>
                  <td>{formatIpdDateTime(line.date)}</td>
                  <td className="opd-bill-charge-name">{line.chargeName}{line.doctor ? <><br /><small>({doctorDisplayName(line.doctor.name)})</small></> : null}</td>
                  <td>{currency(line.rate)}</td>
                  <td>{line.quantity}</td>
                  <td>{currency(line.discountAmount)}</td>
                  <td>{line.discountPercent}%</td>
                  <td><span className={`opd-badge ${line.cancelled ? 'cancelled' : 'paid'}`}>{line.cancelled ? 'Cancelled' : 'Done'}</span></td>
                  <td>{currency(line.amount)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="opd-bill-summary-row">
        <div className="opd-bill-summary">
          <div><span>Sub Total</span><strong>{currency(bill.subTotal)}</strong></div>
          <div><span>Miscellaneous</span><strong>{currency(bill.total - bill.subTotal)}</strong></div>
          <div><span>Total</span><strong>{currency(bill.total)}</strong></div>
          <div><span>Discount</span><strong>{currency(bill.total - bill.grandTotal)}</strong></div>
          <div><span>Payable Amount</span><strong>{currency(bill.grandTotal)}</strong></div>
          <div><span>Total Payment</span><strong>{currency(bill.totalPayment)}</strong></div>
          {bill.dueAmount > 0 ? <div className="due"><span>Due</span><strong>{currency(bill.dueAmount)}</strong></div> : null}
          {bill.creditAmount > 0 ? <div><span>Credit</span><strong>{currency(bill.creditAmount)}</strong></div> : null}
        </div>
      </div>

      <section className="opd-bill-subsection">
        <div className="opd-bill-subsection-head"><h3>Receipt / Adjust Amount List</h3><span>Total Payment <strong>{currency(bill.totalPayment)}</strong></span></div>
        {bill.payments.length === 0 && bill.creditAdjustments.length === 0 ? (
          <p className="opd-bill-empty-note">No payments recorded for this bill.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><th>Date</th><th>Mode</th><th>Bank</th><th>Received By</th><th>Amount</th></tr></thead>
              <tbody>
                {bill.payments.map((payment, index) => (
                  <tr key={payment.id}>
                    <td>R{index + 1}</td>
                    <td>{formatIpdDateTime(payment.date)}</td>
                    <td>{payment.mode}</td>
                    <td>{payment.bankName ?? '-'}</td>
                    <td>{payment.createdBy.name}</td>
                    <td>{currency(payment.amount)}</td>
                  </tr>
                ))}
                {bill.creditAdjustments.map((adjustment, index) => (
                  <tr key={`credit-${index}`}>
                    <td>C{index + 1}</td>
                    <td>-</td>
                    <td colSpan={2}>Credit from Bill #{adjustment.sourceBill.name}</td>
                    <td>-</td>
                    <td>{currency(adjustment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {bill.refunds.length > 0 ? (
        <section className="opd-bill-subsection">
          <div className="opd-bill-subsection-head"><h3>Refund Amount List</h3><span>Total Refund <strong>{currency(bill.totalRefunded)}</strong></span></div>
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><th>Date</th><th>Refund By</th><th>Amount</th></tr></thead>
              <tbody>
                {bill.refunds.map((refund, index) => (
                  <tr key={refund.id}>
                    <td>RF{index + 1}</td>
                    <td>{formatIpdDateTime(refund.date)}</td>
                    <td>{refund.createdBy.name}</td>
                    <td>{currency(refund.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {showRefundPanel && bill.creditAmount > 0 && admission.status !== 'cancelled' ? <RefundPanel billId={bill.id} availableCredit={bill.creditAmount} /> : null}

      {(bill.financeRemark || bill.departmentPartyRemark) ? (
        <section className="opd-bill-subsection">
          <dl className="opd-bill-referral">
            <div><dt>Finance Remark</dt><dd dangerouslySetInnerHTML={{ __html: bill.financeRemark || '-' }} /></div>
            <div><dt>Party/Department Remark</dt><dd dangerouslySetInnerHTML={{ __html: bill.departmentPartyRemark || '-' }} /></div>
          </dl>
        </section>
      ) : null}
    </>
  );
};
